/**
 * What an operator sees about a video's analysis before (held) or after it
 * was published: why it was held, the pipeline's own end checks, the
 * plausibility numbers, every stage that skipped, failed or refused, and
 * which code ran. Held: publish it as it is, or run it again. The API sends
 * `video.review` to operators only (apps/api/src/services/video_view.ts).
 */
import { useState } from "react";
import { post } from "../api/client";
import type { EndCheck, RunReview, Video } from "../api/types";

function endCheck(label: string, c: EndCheck | undefined): string | null {
  if (!c) return null;
  const votes = c.n_reversed != null && c.n_as_configured != null ? `${c.n_reversed} balls against the config, ${c.n_as_configured} with it` : "";
  switch (c.state) {
    case "decided":
      return `${label}: ${c.verdict === "reversed" ? "ends reversed" : "ends as configured"}${votes ? ` (${votes})` : ""}${c.flipped ? ", flipped before the gate" : ""}${c.flip_error ? `; flip failed: ${c.flip_error}` : ""}`;
    case "undecided":
      return `${label}: undecided${votes ? ` (${votes})` : ""}`;
    case "failed":
      return `${label}: FAILED — ${c.reason ?? "no reason logged"}`;
    default:
      return null;
  }
}

function numbers(q: RunReview["quality"]): string | null {
  if (!q) return null;
  const parts = [
    q.median_speed_kmh != null ? `median speed ${q.median_speed_kmh} km/h (${q.n_speed} clips)` : null,
    q.median_length_m != null ? `median bounce ${q.median_length_m} m from the batting crease` : null,
    q.share_short != null ? `${Math.round(q.share_short * 100)}% short` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

export function RunReviewPanel({ v, reload }: { v: Video; reload: () => Promise<void> }) {
  const r = v.review;
  const [busy, setBusy] = useState<"" | "release" | "reanalyze">("");
  const [error, setError] = useState<string | null>(null);
  if (!r) return null;
  const held = v.state === "held";
  const checks = [endCheck("Batting end check", r.checks?.batting_end), endCheck("End canary", r.checks?.end_canary)].filter(Boolean) as string[];
  const nums = numbers(r.quality);
  if (!held && r.reasons.length === 0 && r.warnings.length === 0 && checks.length === 0) return null;
  const act = async (what: "release" | "reanalyze") => {
    const q = what === "release"
      ? "Publish this analysis as it is? The user is emailed that the clips are ready."
      : held
        ? "Run the analysis again? The canary and the end checks run again."
        : "Run the analysis again? The published clips are hidden from the user until the new run is done; if it fails they come back.";
    if (!window.confirm(q)) return;
    setBusy(what);
    setError(null);
    try {
      await post(`/ops/videos/${v.id}/${what}`, {});
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy("");
    }
  };
  return (
    <section className="review mt16" data-testid="run-review" data-decision={r.decision ?? ""}>
      <h3>{held ? "Held for review (operators only)" : "Analysis checks (operators only)"}</h3>
      {r.released_at && (
        <p className="tiny mt6" data-testid="review-released">
          Held, then published by an operator on {new Date(r.released_at).toLocaleString()}{r.release_note ? `: ${r.release_note}` : ""}.
        </p>
      )}
      {r.reasons.length > 0 && (
        <ul className="mt10" data-testid="review-reasons">
          {r.reasons.map((x) => <li key={x.code + x.message}><b>{x.code}</b> {x.message}</li>)}
        </ul>
      )}
      {checks.length > 0 && <ul className="mt10">{checks.map((c) => <li key={c}>{c}</li>)}</ul>}
      {nums && <p className="tiny mt10">{nums}</p>}
      {r.warnings.length > 0 && (
        <details className="mt10" open={held} data-testid="review-warnings">
          <summary>{r.warnings.length} pipeline warning{r.warnings.length === 1 ? "" : "s"}</summary>
          <ul className="mt6">
            {r.warnings.map((w) => (
              <li key={w.source + w.message} className="tiny">
                [{w.source}] {w.message}{w.count > 1 ? ` (×${w.count})` : ""}{w.detail ? <><br /><code>{w.detail}</code></> : null}
              </li>
            ))}
          </ul>
        </details>
      )}
      <p className="tiny mt10">
        Run {r.revision} · pipeline {r.pipeline_revision ? r.pipeline_revision.slice(0, 10) : "commit unknown"} · worker {r.worker_revision ? r.worker_revision.slice(0, 10) : "commit unknown"}
      </p>
      {(held || v.allowed_actions.includes("reanalyze")) && (
        <div className="row mt10" style={{ gap: 8 }}>
          {held && v.allowed_actions.includes("release") && (
            <button className="btn dark" onClick={() => act("release")} disabled={busy !== ""} data-testid="review-release">{busy === "release" ? "Publishing…" : "Publish as it is"}</button>
          )}
          {v.allowed_actions.includes("reanalyze") && (
            <button className="btn ghost" onClick={() => act("reanalyze")} disabled={busy !== ""} data-testid="review-reanalyze">{busy === "reanalyze" ? "Queuing…" : "Run the analysis again"}</button>
          )}
        </div>
      )}
      {error && <p className="tiny mt6" style={{ color: "var(--bad, #b00)" }}>{error}</p>}
    </section>
  );
}
